import React, { useState, useEffect } from "react";
import VelocityGauge from "./VelocityGauge";
import { getSampleDetail } from "../../services/api";
import { getParticleCountSegmentThreshold, getStatus, STATUS_COLORS, STATUS_BG, CANVAS_THEME } from "../../services/thresholdUtils";
import { useTheme } from "../../App";
import useIsMobile from "../../hooks/useIsMobile";
import { generateReportPDF } from "../../services/generatePDF";
import {
  CATEGORY_DEFS,
  ADDITIVE_KEYS,
  METRIC_DEFINITIONS,
  metricToTableField,
  getBoolStatus,
  getWaterStatus,
  WATER_NUMERIC_THRESHOLD,
  statusLabel,
} from "../../services/metricDefinitions";

const _sampleDetailCache = new Map();
const EMPTY_REPORT_HISTORY = [];

export function clearSampleDetailCache(sampleId, detailEmail = "") {
  if (!sampleId) {
    _sampleDetailCache.clear();
    return;
  }
  _sampleDetailCache.delete(`${detailEmail || ""}:${sampleId}`);
}

const ALL_REPORT_FIELDS = [
  { section: "Contamination", fields: CATEGORY_DEFS.find(c => c.id === "contamination").items.map(metricToTableField) },
  { section: "Wear (PPM)", fields: CATEGORY_DEFS.find(c => c.id === "wear").items.map(metricToTableField) },
  { section: "Oil Condition", fields: CATEGORY_DEFS.find(c => c.id === "oil").items.filter(m => !ADDITIVE_KEYS.has(m.key)).map(metricToTableField) },
  { section: "Additive Elements (PPM)", fields: CATEGORY_DEFS.find(c => c.id === "oil").items.filter(m => ADDITIVE_KEYS.has(m.key)).map(metricToTableField) },
  { section: "Raw Indicators", fields: [{ key: "usoot", label: "Soot (Raw)", unit: "" }] },
];

const st = {
  radialSection: { marginTop: 4 },
  gaugeGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 16 },
  tableCard: {
    background: "var(--dashboard-card-bg)", borderRadius: "var(--dashboard-card-radius)",
    border: "var(--dashboard-card-border)", boxShadow: "var(--dashboard-panel-shadow)", overflow: "visible",
  },
  tableSection: {
    fontSize: 13, fontWeight: 700, color: "var(--teal-dark)",
    background: "var(--table-section-bg)", padding: "10px 20px",
    borderBottom: "1px solid var(--border)", textTransform: "uppercase", letterSpacing: "0.05em",
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text-muted)",
    textTransform: "uppercase", letterSpacing: "0.05em", padding: "8px 20px",
    borderBottom: "2px solid var(--border)", background: "var(--bg-card-subtle)",
  },
  td: {
    padding: "8px 20px", borderBottom: "1px solid var(--border-subtle)",
    fontSize: 14, color: "var(--text-primary)",
  },
  tdLabel: { fontWeight: 500, color: "var(--text-secondary)" },
  tdValue: { fontFamily: "var(--font-mono)", fontWeight: 500 },
  tdUnit: { fontSize: 12, color: "var(--text-muted)" },
  tdNull: { color: "var(--text-disabled)", fontStyle: "italic" },
  trEven: { background: "var(--table-row-alt)" },
  summaryRow: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 20 },
  summaryCard: { borderRadius: "var(--dashboard-card-radius)", padding: "16px 18px", textAlign: "left" },
  summaryStatus: { fontFamily: "var(--font-heading)", fontSize: "var(--dashboard-summary-status-size)", fontWeight: 800, marginTop: 8 },
  summaryLabel: { fontSize: 13, fontWeight: 800 },
  summaryMeta: { fontSize: 11, fontWeight: 600, marginTop: 6 },
  tabBar: {
    display: "flex", gap: 4, marginBottom: 20, background: "var(--dashboard-tab-bg)",
    borderRadius: "var(--dashboard-control-radius)", padding: 4, border: "1px solid var(--border)",
  },
  tab: {
    flex: 1, padding: "10px 12px", borderRadius: "calc(var(--dashboard-control-radius) - 2px)", border: "none",
    fontSize: "var(--dashboard-tab-font-size)", fontWeight: 700, cursor: "pointer", transition: "all 0.15s",
    textAlign: "center",
  },
  tabActive: { background: "var(--navy)", color: "#fff" },
  tabInactive: { background: "transparent", color: "var(--dashboard-tab-muted)" },
  emptyToggle: {
    width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
    marginTop: 18, padding: "10px 14px", border: "1px solid var(--border)", borderRadius: 8,
    background: "var(--bg-card-subtle)", color: "var(--text-secondary)", cursor: "pointer",
    fontSize: 13, fontWeight: 800, textAlign: "left",
  },
  emptyPanel: { marginTop: 12 },
  infoWrap: { position: "relative", display: "inline-flex", alignItems: "center", verticalAlign: "middle" },
  infoButton: {
    width: 15, height: 15, borderRadius: 999, border: "1px solid var(--teal-border)",
    background: "var(--teal-bg)", color: "var(--teal-dark)", fontSize: 10, fontWeight: 900,
    lineHeight: 1, cursor: "help", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0,
  },
  infoPopover: {
    position: "absolute", zIndex: 50, top: "calc(100% + 8px)", left: 0,
    width: 300, maxWidth: "min(300px, 74vw)", padding: "10px 12px",
    border: "1px solid var(--border)", borderRadius: 8, background: "var(--bg-card)",
    color: "var(--text-primary)", boxShadow: "0 16px 42px rgba(15, 23, 42, 0.22)",
    fontSize: 12, lineHeight: 1.45, fontWeight: 500, textTransform: "none", letterSpacing: 0,
  },
  particleCard: {
    gridColumn: "span 2", background: "var(--dashboard-gauge-bg)", border: "1px solid var(--border)",
    borderRadius: "var(--dashboard-card-radius)", boxShadow: "var(--dashboard-panel-shadow)",
    padding: "14px 16px 10px", minWidth: 0,
  },
  particleHeader: {
    display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16,
    padding: "0 2px 8px", borderBottom: "1px solid var(--border-subtle)",
  },
  particleTitleGroup: { display: "flex", alignItems: "center", gap: 6, minWidth: 0, flex: "1 1 auto" },
  particleTitle: { fontSize: 13, fontWeight: 800, color: "var(--text-secondary)", lineHeight: 1.35 },
  particleCode: {
    flex: "0 0 auto", fontFamily: "var(--font-mono)", fontSize: 20, lineHeight: 1.2,
    fontWeight: 800, color: "var(--text-primary)", textAlign: "right", whiteSpace: "nowrap",
  },
  particleCodeLabel: { display: "block", marginTop: 2, fontFamily: "inherit", fontSize: 10, fontWeight: 600, color: "var(--text-muted)" },
  particleGaugeGrid: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8, minWidth: 0 },
};

const ENGINE_HIDDEN_KEYS = new Set(["viscosity_40c", "tbn", "tan", "kfish", "vi", "barium", "lithium"]);

function isEngineSample(sample) {
  const component = String(sample?.component_type || sample?.componentType || "").trim().toLowerCase();
  return component === "engine" || component.includes("engine");
}

function filterReportItems(items, sample) {
  if (!isEngineSample(sample)) return items;
  return items.filter((item) => !ENGINE_HIDDEN_KEYS.has(item.key));
}

export function hasDetectedValue(sample, metric) {
  const value = sample?.[metric.key];
  if (metric.kind === "bool") return getBoolStatus(value) === "critical";
  if (value === null || value === undefined || value === "") return false;
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return true;
  return numeric !== 0;
}

function MetricInfoTip({ label, definition, align = "left" }) {
  const [open, setOpen] = useState(false);
  if (!definition) return null;
  return (
    <span
      style={st.infoWrap}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <button
        type="button"
        style={st.infoButton}
        aria-label={`${label} definition`}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
      >
        i
      </button>
      {open && (
        <span style={{ ...st.infoPopover, left: align === "right" ? "auto" : 0, right: align === "right" ? 0 : "auto" }}>
          <strong>{label}</strong>
          <br />
          {definition}
        </span>
      )}
    </span>
  );
}

function getMetricThreshold(metric, template) {
  if (metric.key === "water") return WATER_NUMERIC_THRESHOLD;
  const threshold = metric.threshKey && template ? template[metric.threshKey] : null;
  const particleIndex = ["particle_count_0", "particle_count_1", "particle_count_2"].indexOf(metric.key);
  return particleIndex >= 0 ? getParticleCountSegmentThreshold(threshold, particleIndex) : threshold;
}

function getMetricRecord(metric, sample, template) {
  const value = sample[metric.key];
  const threshold = getMetricThreshold(metric, template);
  const status = metric.key === "water"
    ? getWaterStatus(value)
    : metric.kind === "bool" ? getBoolStatus(value) : (threshold ? getStatus(value, threshold) : (hasDetectedValue(sample, metric) ? "normal" : "na"));
  return { ...metric, value, threshold, status };
}

function getCategoryStatus(records) {
  if (records.some(r => r.status === "critical")) return "critical";
  if (records.some(r => r.status === "warn")) return "warn";
  if (records.some(r => r.status === "normal")) return "normal";
  return "na";
}

function fmtSummaryVal(value, precision = 2) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value !== "number") return String(value);
  return precision === 0 ? String(Math.round(value)) : Number(value).toFixed(precision);
}

function particleCountDisplay(metric, sample) {
  if (!String(metric?.key || "").startsWith("particle_count_")) return {};
  return {
    displayValue: fmtSummaryVal(sample?.[metric.key], metric.precision),
    displayUnit: metric.unit || "ISO code",
    showScaleLabels: false,
  };
}

const PARTICLE_COUNT_KEYS = ["particle_count_0", "particle_count_1", "particle_count_2"];

function isoCodePart(value) {
  if (value === null || value === undefined || value === "" || !Number.isFinite(Number(value))) return "—";
  return String(Math.round(Number(value)));
}

export function combineParticleCountGauges(metrics, sample) {
  const particleMetrics = metrics.filter((metric) => PARTICLE_COUNT_KEYS.includes(metric.key));
  if (particleMetrics.length === 0) return metrics;

  const firstIndex = metrics.findIndex((metric) => PARTICLE_COUNT_KEYS.includes(metric.key));
  const displayValue = PARTICLE_COUNT_KEYS.map((key) => isoCodePart(sample?.[key])).join("/");
  const needleMetric = particleMetrics.find((metric) => hasDetectedValue(sample, metric)) || particleMetrics[0];
  const combined = {
    ...needleMetric,
    key: "particle_count_iso",
    sourceKey: needleMetric.key,
    label: "Particle Count",
    unit: "ISO code",
    displayValue,
    displayUnit: "ISO cleanliness code",
    showScaleLabels: false,
    particleMetrics,
    definition: "Combined ISO cleanliness code from the >4, >6, and >14 micron particle readings, displayed in X/Y/Z format.",
  };

  const withoutParticleMetrics = metrics.filter((metric) => !PARTICLE_COUNT_KEYS.includes(metric.key));
  withoutParticleMetrics.splice(firstIndex, 0, combined);
  return withoutParticleMetrics;
}

function metricValue(sample, metric) {
  return sample?.[metric.sourceKey || metric.key];
}

function ParticleCountCard({ metric, sample, template, isMobile }) {
  const sizeLabels = {
    particle_count_0: ">4 µm",
    particle_count_1: ">6 µm",
    particle_count_2: ">14 µm",
  };

  return (
    <section
      style={{ ...st.particleCard, gridColumn: isMobile ? "1 / -1" : "span 2" }}
      aria-label={`Particle Count: ${metric.displayValue}`}
    >
      <div style={st.particleHeader}>
        <div style={st.particleTitleGroup}>
          <span style={st.particleTitle}>{metric.label}</span>
          <MetricInfoTip label={metric.label} definition={metric.definition} />
        </div>
        <div style={st.particleCode}>
          {metric.displayValue}
          <span style={st.particleCodeLabel}>ISO cleanliness code</span>
        </div>
      </div>
      <div style={{ ...st.particleGaugeGrid, gap: isMobile ? 2 : 8 }}>
        {metric.particleMetrics.map((particleMetric) => (
          <VelocityGauge
            key={particleMetric.key}
            label={sizeLabels[particleMetric.key] || particleMetric.label}
            value={sample?.[particleMetric.key]}
            unit="ISO"
            min={particleMetric.min}
            max={particleMetric.max}
            precision={0}
            threshold={getMetricThreshold(particleMetric, template)}
            displayValue={isoCodePart(sample?.[particleMetric.key])}
            displayUnit="ISO"
            showScaleLabels={false}
            embedded
            compact
            hideNeedleTail
            engineMileage={sample?.engine_miles}
          />
        ))}
      </div>
    </section>
  );
}

function BoolIndicator({ label, value, definition = "", engineMileage }) {
  const { isDark } = useTheme();
  const ct = CANVAS_THEME[isDark ? "dark" : "light"];
  const status = getBoolStatus(value);
  const hexColor = ct[status] || ct.na;
  const statusBg = STATUS_BG[status];
  const displayLabel = {
    normal: "Negative - Not Detected",
    critical: "Positive - Detected",
    na: "Not Tested",
  }[status];

  return (
    <div style={{
      background: "var(--dashboard-gauge-bg)",
      border: `1px solid ${status !== "na" ? hexColor + "55" : "var(--border)"}`,
      borderRadius: "var(--dashboard-card-radius)",
      padding: "14px 18px 16px",
      boxShadow: "var(--dashboard-panel-shadow)",
      minHeight: 80,
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      gap: 10,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)" }}>{label}</span>
          <MetricInfoTip label={label} definition={definition} />
        </span>
        <span style={{ fontSize: 10, color: "var(--text-muted)", fontWeight: 500, letterSpacing: "0.05em" }}>PASS / FAIL</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{
          width: 11, height: 11, borderRadius: "50%",
          background: hexColor,
          flexShrink: 0,
          boxShadow: `0 0 6px ${hexColor}99`,
        }} />
        <span style={{
          fontSize: 15, fontWeight: 700,
          color: hexColor,
          fontFamily: "system-ui, -apple-system, sans-serif",
        }}>
          {displayLabel}
        </span>
      </div>
      {status === "critical" && (
        <div style={{
          fontSize: 11, color: hexColor, fontStyle: "italic",
          borderTop: `1px solid ${hexColor}30`, paddingTop: 8,
        }}>
          Coolant contamination detected - consult your mechanic immediately.
        </div>
      )}
      {status !== "na" && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <span style={{
            fontSize: 10, fontWeight: 700, padding: "2px 10px", borderRadius: 20,
            background: statusBg, color: hexColor,
          }}>
            {statusLabel(status, engineMileage)}
          </span>
        </div>
      )}
    </div>
  );
}

function CategorySummary({ sample, template, activeTab, onSelect, isMobile }) {
  const { isDark } = useTheme();
  const ct = CANVAS_THEME[isDark ? "dark" : "light"];

  if (!template) return null;

  const categorySummaries = CATEGORY_DEFS.map(category => {
    const records = filterReportItems(category.items, sample)
      .map(metric => getMetricRecord(metric, sample, template));
    const status = getCategoryStatus(records);
    const flagged = records.filter(r => r.status === "warn" || r.status === "critical");
    return { ...category, records, status, flagged };
  });

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ ...st.summaryRow, gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)" }}>
        {categorySummaries.map(({ id, label, status, flagged, records }) => {
          const isActive = activeTab === id;
          const hexColor = ct[status] || ct.na;
          const normalCount = records.filter(r => r.status === "normal").length;
          const issueText = flagged.length > 0
            ? `${flagged.length} item${flagged.length === 1 ? "" : "s"} need${flagged.length === 1 ? "s" : ""} attention`
            : `${normalCount} item${normalCount === 1 ? "" : "s"} checked`;
          const detailItems = flagged.length > 0 ? flagged : records.filter(r => r.status === "normal").slice(0, 4);

          return (
            <div
              key={id}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(id)}
              onKeyDown={(e) => e.key === "Enter" && onSelect(id)}
              style={{
                ...st.summaryCard,
                background: STATUS_BG[status],
                border: `1.5px solid ${hexColor}${isActive ? "aa" : "55"}`,
                boxShadow: isActive ? `0 2px 12px ${hexColor}35` : "none",
                cursor: "pointer",
                transition: "border-color 0.15s, box-shadow 0.15s",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <div style={{ ...st.summaryLabel, color: hexColor }}>{label}</div>
                <div style={{ width: 13, height: 13, borderRadius: "50%", background: hexColor, boxShadow: `0 0 8px ${hexColor}77`, flexShrink: 0 }} />
              </div>
              <div style={{ ...st.summaryStatus, color: hexColor }}>{statusLabel(status, sample?.engine_miles)}</div>
              <div style={{ ...st.summaryMeta, color: hexColor, opacity: 0.75 }}>{issueText}</div>
              {detailItems.length > 0 && (
                <div style={{ marginTop: 12, borderTop: `1px solid ${hexColor}28`, paddingTop: 9 }}>
                  {detailItems.map(({ label: itemLabel, value, unit, precision, status: itemStatus }) => (
                    <div key={itemLabel} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 11, color: "var(--text-secondary)", marginTop: 4 }}>
                      <span>{itemLabel}</span>
                      <span style={{ fontFamily: "var(--font-mono)", color: ct[itemStatus] || hexColor, fontWeight: 700 }}>
                        {fmtSummaryVal(value, precision)}
                        {unit && <span style={{ color: "var(--text-muted)", fontWeight: 400, marginLeft: 3 }}>{unit}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MetricSection({ sample, template, items, isMobile }) {
  const [showEmpty, setShowEmpty] = useState(false);
  const reportItems = filterReportItems(items, sample);
  const detectedItems = reportItems.filter(m => hasDetectedValue(sample, m));
  const emptyItems = reportItems.filter(m => !hasDetectedValue(sample, m));
  const renderedGaugeItems = combineParticleCountGauges(reportItems.filter(m => m.kind === "gauge"), sample);
  const gaugeItems = renderedGaugeItems.filter((metric) => metric.key === "particle_count_iso"
    ? PARTICLE_COUNT_KEYS.some((key) => hasDetectedValue(sample, { key, kind: "gauge" }))
    : hasDetectedValue(sample, metric));
  const boolItems = detectedItems.filter(m => m.kind === "bool");
  const emptyGaugeItems = renderedGaugeItems.filter((metric) => !gaugeItems.includes(metric));
  const emptyBoolItems = emptyItems.filter(m => m.kind === "bool");

  if (reportItems.length === 0) {
    return (
      <div style={{ ...st.tableCard, padding: 24, color: "var(--text-muted)", fontSize: 14 }}>
        No report data available for this category.
      </div>
    );
  }

  return (
    <div>
      {gaugeItems.length > 0 && (
        <div style={st.radialSection}>
          <div style={{ ...st.gaugeGrid, gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? 160 : 210}px, 1fr))` }}>
            {gaugeItems.map(metric => (
              metric.key === "particle_count_iso" ? (
                <ParticleCountCard key={metric.key} metric={metric} sample={sample} template={template} isMobile={isMobile} />
              ) : (
                <VelocityGauge
                  key={metric.key}
                  label={metric.label}
                  value={metricValue(sample, metric)}
                  unit={metric.unit}
                  min={metric.min}
                  max={metric.max}
                  precision={metric.precision}
                  threshold={getMetricThreshold(metric, template)}
                  definition={metric.definition || METRIC_DEFINITIONS[metric.key] || ""}
                  engineMileage={sample?.engine_miles}
                  {...particleCountDisplay(metric, sample)}
                />
              )
            ))}
          </div>
        </div>
      )}

      {boolItems.length > 0 && (
        <div style={{
          ...st.gaugeGrid,
          gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? 220 : 260}px, 1fr))`,
          marginTop: gaugeItems.length > 0 ? 16 : 0,
        }}>
          {boolItems.map(metric => (
            <BoolIndicator key={metric.key} label={metric.label} value={sample[metric.key]} definition={metric.definition || METRIC_DEFINITIONS[metric.key] || ""} engineMileage={sample?.engine_miles} />
          ))}
        </div>
      )}

      {emptyItems.length > 0 && (
        <div>
          <button type="button" style={st.emptyToggle} onClick={() => setShowEmpty((current) => !current)}>
            <span>No values detected ({emptyItems.length})</span>
            <span style={{ fontSize: 16, lineHeight: 1 }}>{showEmpty ? "-" : "+"}</span>
          </button>
          {showEmpty && (
            <div style={st.emptyPanel}>
              {emptyGaugeItems.length > 0 && (
                <div style={{ ...st.gaugeGrid, gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? 160 : 210}px, 1fr))` }}>
                  {emptyGaugeItems.map(metric => (
                    metric.key === "particle_count_iso" ? (
                      <ParticleCountCard key={metric.key} metric={metric} sample={sample} template={template} isMobile={isMobile} />
                    ) : (
                      <VelocityGauge
                        key={metric.key}
                        label={metric.label}
                        value={metricValue(sample, metric)}
                        unit={metric.unit}
                        min={metric.min}
                        max={metric.max}
                        precision={metric.precision}
                        threshold={getMetricThreshold(metric, template)}
                        definition={metric.definition || METRIC_DEFINITIONS[metric.key] || ""}
                        engineMileage={sample?.engine_miles}
                        {...particleCountDisplay(metric, sample)}
                      />
                    )
                  ))}
                </div>
              )}
              {emptyBoolItems.length > 0 && (
                <div style={{
                  ...st.gaugeGrid,
                  gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? 220 : 260}px, 1fr))`,
                  marginTop: emptyGaugeItems.length > 0 ? 16 : 0,
                }}>
                  {emptyBoolItems.map(metric => (
                    <BoolIndicator key={metric.key} label={metric.label} value={sample[metric.key]} definition={metric.definition || METRIC_DEFINITIONS[metric.key] || ""} engineMileage={sample?.engine_miles} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DataTable({ sample, template }) {
  return (
    <div style={st.tableCard}>
      {ALL_REPORT_FIELDS.map(section => (
        <div key={section.section}>
          <div style={st.tableSection}>{section.section}</div>
          <table style={st.table}>
            <thead>
              <tr>
                <th style={{ ...st.th, width: "35%" }}>Parameter</th>
                <th style={{ ...st.th, width: "25%" }}>Value</th>
                <th style={{ ...st.th, width: "15%" }}>Unit</th>
                <th style={{ ...st.th, width: "25%" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filterReportItems(section.fields, sample).map((field, i) => {
                const val = sample[field.key];
                const hasVal = val !== null && val !== undefined;
                const thresh = field.threshKey && template ? template[field.threshKey] : null;
                const status = hasVal ? (thresh ? getStatus(val, thresh) : (hasDetectedValue(sample, field) ? "normal" : "na")) : "na";
                const sColor = STATUS_COLORS[status];
                return (
                  <tr key={field.key} style={i % 2 === 0 ? {} : st.trEven}>
                    <td style={{ ...st.td, ...st.tdLabel }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, position: "relative" }}>
                        {field.label}
                        <MetricInfoTip label={field.label} definition={field.definition} />
                      </span>
                    </td>
                    <td style={st.td}>
                      {hasVal ? (
                        <span style={{ ...st.tdValue, color: thresh && status !== "na" ? sColor : "var(--text-primary)" }}>
                          {typeof val === "number" ? val : String(val)}
                        </span>
                      ) : (
                        <span style={st.tdNull}>{"—"}</span>
                      )}
                    </td>
                    <td style={st.td}><span style={st.tdUnit}>{field.unit}</span></td>
                    <td style={st.td}>
                      {hasVal && status !== "na" ? (
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                          background: STATUS_BG[status], color: sColor,
                        }}>
                          {statusLabel(status, sample?.engine_miles)}
                        </span>
                      ) : (
                        <span style={{ fontSize: 11, color: "var(--text-disabled)" }}>{"—"}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

const TABS = [...CATEGORY_DEFS, { id: "table", label: "Full Data", shortLabel: "Table" }];

export default function GaugeGrid({ sample, reportHistory = EMPTY_REPORT_HISTORY, detailEmail = "", activeTab: controlledActiveTab, onTabChange, onTemplateResolved, onDetailLoaded, refreshKey = 0 }) {
  const [internalActiveTab, setInternalActiveTab] = useState("contamination");
  const [sampleDetail, setSampleDetail] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const isMobile = useIsMobile();
  const activeTab = controlledActiveTab || internalActiveTab;
  const selectTab = (tabId) => {
    setInternalActiveTab(tabId);
    onTabChange?.(tabId);
  };

  const handlePdfDownload = async () => {
    if (isGeneratingPdf || !sampleDetail?.thresholds) return;
    setIsGeneratingPdf(true);
    try {
      const currentId = sample?.lab_sample_id;
      const previousReports = reportHistory
        .filter((report) => report?.lab_sample_id && report.lab_sample_id !== currentId)
        .slice(0, 3);
      const resolvedHistory = await Promise.all(previousReports.map(async (report) => {
        const cacheKey = `${detailEmail || ""}:${report.lab_sample_id}`;
        let detail = _sampleDetailCache.get(cacheKey);
        if (!detail) {
          try {
            const response = await getSampleDetail(report.lab_sample_id, detailEmail);
            detail = response.data;
            _sampleDetailCache.set(cacheKey, detail);
          } catch (_) {
            detail = null;
          }
        }
        return { ...report, _pdfThresholds: detail?.thresholds || {} };
      }));
      await generateReportPDF(
        { ...sample, oilGeekNote: sampleDetail?.oilGeekNote || "" },
        sampleDetail?.thresholds || null,
        resolvedHistory
      );
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  useEffect(() => {
    const sampleId = sample?.lab_sample_id;
    if (!sampleId) {
      setSampleDetail(null);
      onTemplateResolved?.("");
      onDetailLoaded?.(null);
      return;
    }

    const cacheKey = `${detailEmail || ""}:${sampleId}`;
    if (_sampleDetailCache.has(cacheKey)) {
      const cached = _sampleDetailCache.get(cacheKey);
      setSampleDetail(cached);
      onTemplateResolved?.(cached?.templateName || "");
      onDetailLoaded?.(cached || null);
      return;
    }

    let isActive = true;
    onTemplateResolved?.("");
    getSampleDetail(sampleId, detailEmail)
      .then(res => {
        if (!isActive) return;
        _sampleDetailCache.set(cacheKey, res.data);
        setSampleDetail(res.data);
        onTemplateResolved?.(res.data?.templateName || "");
        onDetailLoaded?.(res.data || null);
      })
      .catch(() => {});
    return () => { isActive = false; };
  }, [sample?.lab_sample_id, detailEmail, onTemplateResolved, onDetailLoaded, refreshKey]);

  if (!sample) return null;

  const template = sampleDetail?.thresholds || null;
  const activeCategory = CATEGORY_DEFS.find(category => category.id === activeTab);

  return (
    <div>
      <div style={st.tabBar}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => selectTab(tab.id)}
            style={{
              ...st.tab,
              ...(activeTab === tab.id ? st.tabActive : st.tabInactive),
              fontSize: isMobile ? 11 : 13,
              padding: isMobile ? "8px 4px" : "10px 12px",
            }}
          >
            {isMobile ? tab.shortLabel : tab.label}
          </button>
        ))}
      </div>

      {activeCategory && (
        <MetricSection
          sample={sample}
          template={template}
          items={activeCategory.items}
          isMobile={isMobile}
        />
      )}

      {activeTab === "table" && (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <button
              onClick={handlePdfDownload}
              disabled={isGeneratingPdf || !template}
              style={{
                display: "flex", alignItems: "center", gap: 7,
                background: "var(--teal-dark)", color: "#fff",
                border: "none", borderRadius: 8,
                padding: "9px 18px", fontSize: 13, fontWeight: 600,
                cursor: isGeneratingPdf ? "wait" : !template ? "not-allowed" : "pointer", transition: "opacity 0.2s",
                opacity: isGeneratingPdf || !template ? 0.72 : 1,
                boxShadow: "0 2px 8px rgba(14,165,233,0.25)",
              }}
              onMouseEnter={e => { if (!isGeneratingPdf && template) e.currentTarget.style.opacity = "0.85"; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = isGeneratingPdf || !template ? "0.72" : "1"; }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                <path d="M7 1v8M7 9l-3-3M7 9l3-3M1 11h12" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {!template ? "Loading PDF data..." : isGeneratingPdf ? "Preparing PDF..." : "Download PDF Report"}
            </button>
          </div>
          <div style={isMobile ? { overflowX: "auto", WebkitOverflowScrolling: "touch" } : {}}>
            <DataTable sample={sample} template={template} />
          </div>
        </div>
      )}
    </div>
  );
}
